import { Controller } from "@hotwired/stimulus"

// A modal rendered into a shared container and removed on close.
//
// The container stays in the layout permanently and holds nothing until a
// modal is asked for, so opening one does not disturb the page behind it.

// HOW MANY MODALS ARE OPEN, not whether one is.
//
// These render into a PERMANENT <turbo-frame id="modal">, so replacing one
// modal with another — the picker opening a preview, the feedback form
// becoming its thank-you — disconnects the old controller AFTER connecting
// the new one. A naive lock/unlock pair unlocks the body while the second
// modal is still opening, and the page scrolls behind it.
//
// A depth count survives the overlap: the lock is taken when it goes 0 → 1
// and released when it returns to 0, so a swap never dips below one.
let openModals = 0
let restoreY = 0

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    // Focus the panel so Escape works without a click first, and so a
    // keyboard user lands inside the modal rather than behind it.
    //
    // The ring this used to draw is hidden in CSS rather than by skipping
    // the focus — see .modal-control and the panel's focus:outline-none.
    // A tabindex="-1" element cannot be reached by tabbing, so there is no
    // keyboard path here that would need a visible ring.
    this.panelTarget?.setAttribute("tabindex", "-1")
    this.panelTarget?.focus()

    this.lockScroll()
  }

  disconnect() {
    this.unlockScroll()
  }

  close() {
    this.element.remove()
  }

  // CLOSE ONCE THE SERVER HAS ACCEPTED, for a modal whose own form posts
  // somewhere that answers with a stream for the page BEHIND it.
  //
  // A modal rendered into <turbo-frame id="modal"> is closed by the
  // response emptying that frame. One appended beside it — see
  // shared/_alert_modal's `stacked:` — is not, and the picker's
  // raw-weight confirmation stayed on screen over a day that had already
  // re-rendered behind it.
  //
  // ON SUCCESS ONLY. A failed save redirects back with an alert, and
  // closing regardless would dismiss the modal and the message with it.
  closeOnSuccess(event) {
    if (event.detail?.success === false) return

    this.close()
  }

  // ESCAPE CLOSES THE TOPMOST OVERLAY, NOT EVERY OVERLAY.
  //
  // The binding is `keydown.esc@document`, so every open modal hears the
  // same keypress. With one on screen that is the same thing; with two it is
  // not. The preview panel opens a lightbox over itself, and one Escape used
  // to close both — you asked to leave the photo and lost the panel behind
  // it, which reads as the app closing on you.
  //
  // The LAST one in the DOM is the topmost: each is appended when it opens,
  // and z-index does not vary between them. Comparing against that rather
  // than tracking a stack keeps this a function of what is on screen — a
  // counter would have to survive a Turbo frame swap replacing a modal
  // without either lifecycle hook firing.
  closeTopmost(event) {
    const open = document.querySelectorAll('[data-controller~="modal"]')
    if (open[open.length - 1] !== this.element) return

    event.stopPropagation()
    this.close()
  }

  // A click inside the panel must not close the modal — only the space
  // around it.
  //
  // WHERE THE GESTURE STARTED, not where it ended. A click event fires on
  // the nearest common ancestor of mousedown and mouseup, so selecting text
  // inside the panel and releasing past its edge — which is what happens
  // whenever you drag to the end of a line — targeted the overlay and shut
  // the modal, losing whatever had been typed.
  //
  // Reported by Cody, 4 Sep. General to every modal in the app.
  startedInside(event) {
    this.downInsidePanel =
      this.hasPanelTarget && this.panelTarget.contains(event.target)

    // WHERE THE GESTURE BEGAN, IN PAGE COORDINATES. A tap and a scroll
    // both end in a click; only the distance travelled tells them apart.
    const point = event.touches?.[0] || event
    this.downAt = { x: point.clientX, y: point.clientY }
  }

  // A SCROLL IS NOT A DISMISSAL.
  //
  // On touch, dragging a list that reaches the edge of the panel — or
  // starting the drag on the backdrop at all — ends in a click, and the
  // modal closed mid-scroll with a half-filled form inside it. Cody,
  // 11 Sep, scrolling a category list.
  //
  // 10px, because a finger never holds perfectly still: a real tap wanders
  // a pixel or two and must still close the modal, while any deliberate
  // scroll clears this on the first frame.
  movedSinceDown(event) {
    if (!this.downAt) return false

    const dx = Math.abs(event.clientX - this.downAt.x)
    const dy = Math.abs(event.clientY - this.downAt.y)
    return dx > 10 || dy > 10
  }

  closeOnBackdrop(event) {
    if (this.hasPanelTarget && this.panelTarget.contains(event.target)) return
    if (this.downInsidePanel) return
    if (this.movedSinceDown(event)) return

    this.close()
  }

  // POSITION: FIXED, NOT OVERFLOW: HIDDEN.
  //
  // iOS Safari ignores `overflow: hidden` on <body> — the page keeps
  // scrolling under the modal, which is the whole bug. Pinning the body
  // takes it out of flow, which does work, but it also scrolls the document
  // to the top; so the offset is stashed and re-applied as a negative `top`
  // to hold the page exactly where it was.
  lockScroll() {
    if (openModals++ > 0) return

    restoreY = window.scrollY
    const body = document.body
    body.style.position = "fixed"
    body.style.top = `-${restoreY}px`
    body.style.left = "0"
    body.style.right = "0"
    // The pinned body no longer reserves the scrollbar's width, so the page
    // jumps sideways on a desktop without one being kept.
    body.style.overflowY = "scroll"
  }

  // RESTORING THE POSITION IS HALF THE FIX. Without it every close returns
  // you to the top of the page, which is a worse bug than the one being
  // fixed — you lose your place in the day rather than merely scrolling
  // behind a panel.
  unlockScroll() {
    if (--openModals > 0) return
    if (openModals < 0) openModals = 0

    const body = document.body
    body.style.position = ""
    body.style.top = ""
    body.style.left = ""
    body.style.right = ""
    body.style.overflowY = ""
    window.scrollTo(0, restoreY)
  }
}
